import React, { useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import Tooltip from './UI/Tooltip';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';

const GlobalDashboard = ({ overviewData, viewMode = 'detailed' }) => {
  const { isDark } = useTheme();

  // Default data structure
  const defaultData = useMemo(() => ({
    hasData: false,
    totalBatches: 0,
    totalExecutions: 0,
    dateRange: { earliest: null, latest: null },
    metrics: {
      averageClientScore: 0,
      clientVisibilityRate: 0,
      totalCompetitors: 0,
      successRate: 0,
      totalSources: 0,
      uniqueDomains: 0
    },
    engines: {},
    topCompetitors: [],
    trackedCompetitors: [],
    topSources: [],
    bestQueries: [],
    sourceCategories: {}
  }), []);

  const data = overviewData || defaultData;

  const getEngineIcon = (engine) => {
    const engineName = engine.toLowerCase();
    const logoMap = {
      'openai': '/logos/chatgpt.png',
      'chatgpt': '/logos/chatgpt.png',
      'gemini': '/logos/gemini.png',
      'perplexity': '/logos/perplexity.png'
    };
    
    const logoSrc = logoMap[engineName];
    if (logoSrc) {
      return (
        <img 
          src={logoSrc} 
          alt={`${engine} logo`}
          className="w-6 h-6 rounded-full object-cover"
        />
      );
    }
    return <div className={`w-6 h-6 rounded-full ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`}></div>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Group sources by category for pie chart
  const sourceChartData = useMemo(() => {
    if (!data.sourceCategories || Object.keys(data.sourceCategories).length === 0) {
      return [];
    }
    
    return Object.entries(data.sourceCategories)
      .map(([name, stats]) => ({ 
        name, 
        value: stats.count,
        domains: stats.domains 
      }))
      .sort((a, b) => b.value - a.value);
  }, [data.sourceCategories]);

  // Colors for pie chart
  const COLORS = {
    'Competitor': '#8b5cf6',
    'Ranking': '#10b981', 
    'Comparison': '#f59e0b',
    'UserReviews': '#ef4444',
    'BlogPost': '#3b82f6',
    'News': '#06b6d4',
    'Announcements': '#ec4899',
    'Podcasts': '#84cc16',
    'SocialMedia': '#f97316',
    'Wikipedia': '#8b5cf6',
    'Reddit': '#ff4500',
    'Unknown': '#6b7280'
  };

  if (!data.hasData) {
    return (
      <div className={`glass-card rounded-xl overflow-visible ${viewMode === 'simple' ? 'p-8' : 'p-12'}`}>
        <div className={`flex items-center gap-2 ${viewMode === 'simple' ? 'mb-6' : 'mb-8'}`}>
          <svg className={`w-5 h-5 ${isDark ? 'text-accent-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h2 className={`heading-mono text-lg font-bold text-glow ${isDark ? 'text-white' : 'text-gray-800'}`}>Global Analytics Dashboard</h2>
        </div>

        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h3 className={`heading-mono text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>No Data Available</h3>
          <p className={`text-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Execute some searches to see comprehensive analytics here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`glass-card rounded-xl overflow-visible ${viewMode === 'simple' ? 'p-8' : 'p-12'}`}>
      <div className={`flex items-center gap-2 ${viewMode === 'simple' ? 'mb-8' : 'mb-12'}`}>
        <svg className={`w-5 h-5 ${isDark ? 'text-accent-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <h2 className={`heading-mono text-lg font-bold text-glow ${isDark ? 'text-white' : 'text-gray-800'}`}>Global Analytics Dashboard</h2>
        {viewMode === 'detailed' && data.dateRange.earliest && (
          <span className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} ml-auto`}>
            {formatDate(data.dateRange.earliest)} - {formatDate(data.dateRange.latest)}
          </span>
        )}
      </div>

      {/* Key Metrics Grid */}
      <div className={`grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 overflow-visible ${viewMode === 'simple' ? 'gap-6 mb-12' : 'gap-8 mb-16'}`}>
        
        <div className={`glass-dark rounded-lg relative overflow-visible ${viewMode === 'simple' ? 'p-4' : 'p-6'}`}>
          <Tooltip position="bottom" content="Percentage of searches where your brand was mentioned across all executions">
            <div className={`text-mono text-sm mb-1 flex items-center gap-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Visibility
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </Tooltip>
          <div className={`text-2xl font-bold ${isDark ? 'text-accent-400' : 'text-blue-600'}`}>{data.metrics.clientVisibilityRate}%</div>
        </div>
        
        <div className={`glass-dark rounded-lg relative overflow-visible ${viewMode === 'simple' ? 'p-4' : 'p-6'}`}>
          <Tooltip position="bottom" content="Total number of unique competitors found across all searches">
            <div className={`text-mono text-sm mb-1 flex items-center gap-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Competitors
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </Tooltip>
          <div className={`text-2xl font-bold ${isDark ? 'text-accent-400' : 'text-blue-600'}`}>{data.metrics.totalCompetitors}</div>
        </div>
        
        <div className={`glass-dark rounded-lg relative overflow-visible ${viewMode === 'simple' ? 'p-4' : 'p-6'}`}>
          <Tooltip position="bottom" content="Success rate of all search executions">
            <div className={`text-mono text-sm mb-1 flex items-center gap-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Success
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </Tooltip>
          <div className={`text-2xl font-bold ${isDark ? 'text-accent-400' : 'text-blue-600'}`}>{data.metrics.successRate}%</div>
        </div>
        
        <div className={`glass-dark rounded-lg relative overflow-visible ${viewMode === 'simple' ? 'p-4' : 'p-6'}`}>
          <Tooltip position="bottom" content="Total number of batches executed">
            <div className={`text-mono text-sm mb-1 flex items-center gap-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Batches
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </Tooltip>
          <div className={`text-2xl font-bold ${isDark ? 'text-accent-400' : 'text-blue-600'}`}>{data.totalBatches}</div>
        </div>
        
        <div className={`glass-dark rounded-lg relative overflow-visible ${viewMode === 'simple' ? 'p-4' : 'p-6'}`}>
          <Tooltip position="bottom" content="Total number of sources found across all searches">
            <div className={`text-mono text-sm mb-1 flex items-center gap-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              Sources
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </Tooltip>
          <div className={`text-2xl font-bold ${isDark ? 'text-accent-400' : 'text-blue-600'}`}>{data.metrics.uniqueDomains}</div>
        </div>
      </div>

      {/* Engine Performance */}
      {Object.keys(data.engines).length > 0 && (
        <div className={viewMode === 'simple' ? 'mb-12' : 'mb-16'}>
          <h3 className={`text-mono text-md font-bold ${viewMode === 'simple' ? 'mb-4' : 'mb-6'} flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            {viewMode === 'detailed' ? 'Engine Performance' : 'Engine Performance'}
          </h3>
          <div className={`grid grid-cols-1 md:grid-cols-3 ${viewMode === 'simple' ? 'gap-4' : 'gap-6'}`}>
            {Object.entries(data.engines).map(([engine, stats]) => (
              <div key={engine} className={`glass-card rounded-lg ${viewMode === 'simple' ? 'p-4' : 'p-6'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {getEngineIcon(engine)}
                    <span className={`text-mono font-medium capitalize ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {engine}
                    </span>
                  </div>
                  <div className={`text-xl font-bold ${
                    stats.visibilityRate >= 50 ? 'text-green-400' : 
                    stats.visibilityRate >= 25 ? 'text-yellow-400' : 
                    'text-red-400'
                  }`}>
                    {stats.visibilityRate}%
                  </div>
                </div>
                {viewMode === 'detailed' ? (
                  <div className="space-y-1">
                    <div className="flex justify-between">
                      <span className={`text-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Success Rate
                      </span>
                      <span className={`text-mono text-xs font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                        {stats.successRate}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className={`text-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        Mentions
                      </span>
                      <span className={`text-mono text-xs font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                        {stats.clientMentions}/{stats.count}
                      </span>
                    </div>
                    <div className={`w-full ${isDark ? 'bg-gray-600' : 'bg-gray-300'} rounded-full h-2 mt-2`}>
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          stats.visibilityRate >= 50 ? 'bg-green-400' : 
                          stats.visibilityRate >= 25 ? 'bg-yellow-400' : 
                          'bg-red-400'
                        }`}
                        style={{ width: `${stats.visibilityRate}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className={`text-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    {stats.clientMentions}/{stats.count} mentions
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Visibility Over Time Chart */}
      {data.visibilityTimeSeries && data.visibilityTimeSeries.length > 0 && (
        <div className={viewMode === 'simple' ? 'mb-12' : 'mb-16'}>
          <h3 className={`text-mono text-md font-bold ${viewMode === 'simple' ? 'mb-4' : 'mb-6'} flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            Visibility Over Time
            {viewMode === 'detailed' && (
              <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                (Weekly Brand Mentions)
              </span>
            )}
          </h3>
          <div className="glass-card rounded-lg p-6">
            <div className={`${viewMode === 'simple' ? 'h-48' : 'h-64'}`}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.visibilityTimeSeries} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke={isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'} 
                  />
                  <XAxis 
                    dataKey="week" 
                    tick={{ 
                      fontSize: 10, 
                      fill: isDark ? '#9ca3af' : '#6b7280',
                      fontFamily: 'monospace'
                    }}
                    axisLine={{ stroke: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)' }}
                    tickLine={{ stroke: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)' }}
                  />
                  <YAxis 
                    domain={[0, 100]}
                    tick={{ 
                      fontSize: 10, 
                      fill: isDark ? '#9ca3af' : '#6b7280',
                      fontFamily: 'monospace'
                    }}
                    axisLine={{ stroke: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)' }}
                    tickLine={{ stroke: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)' }}
                    label={{ 
                      value: 'Visibility %', 
                      angle: -90, 
                      position: 'insideLeft',
                      style: { 
                        textAnchor: 'middle', 
                        fill: isDark ? '#9ca3af' : '#6b7280',
                        fontFamily: 'monospace',
                        fontSize: '10px'
                      }
                    }}
                  />
                  <RechartsTooltip 
                    contentStyle={{
                      backgroundColor: isDark ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                      border: isDark ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(0, 0, 0, 0.2)',
                      borderRadius: '8px',
                      padding: '12px',
                      boxShadow: isDark ? '0 4px 12px rgba(0, 0, 0, 0.5)' : '0 4px 12px rgba(0, 0, 0, 0.15)',
                    }}
                    labelStyle={{ 
                      color: isDark ? '#ffffff' : '#000000',
                      fontFamily: 'monospace',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}
                    itemStyle={{
                      color: isDark ? '#ffffff' : '#000000',
                      fontFamily: 'monospace',
                      fontSize: '11px'
                    }}
                    formatter={(value, name, props) => [
                      `${value}% (${props.payload.mentions}/${props.payload.executions})`, 
                      'Visibility Rate'
                    ]}
                    labelFormatter={(label) => `${label}`}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="visibility" 
                    stroke={isDark ? '#8b5cf6' : '#3b82f6'}
                    strokeWidth={3}
                    dot={{ 
                      fill: isDark ? '#8b5cf6' : '#3b82f6', 
                      strokeWidth: 2, 
                      r: 4 
                    }}
                    activeDot={{ 
                      r: 6, 
                      fill: isDark ? '#8b5cf6' : '#3b82f6',
                      stroke: isDark ? '#ffffff' : '#000000',
                      strokeWidth: 2
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      <div className={`grid ${viewMode === 'simple' ? 'md:grid-cols-1 gap-8' : 'md:grid-cols-2 gap-12'}`}>
        {/* Tracked Competitors */}
        <div>
          <h3 className={`text-mono text-md font-bold ${viewMode === 'simple' ? 'mb-4' : 'mb-6'} flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Tracked Competitors
            <span className="glass-badge glass-badge-info text-mono text-xs px-2 py-0.5">Monitored</span>
          </h3>
          {data.trackedCompetitors && data.trackedCompetitors.length > 0 ? (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {data.trackedCompetitors.map((competitor, idx) => (
                <div key={competitor.name} className="flex items-center justify-between p-3 glass-dark rounded-lg border border-purple-500/20">
                  {viewMode === 'detailed' ? (
                    <>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className={`text-mono text-sm font-medium ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>#{idx + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className={`text-mono font-medium truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>{competitor.name}</div>
                          <div className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {competitor.queries} queries • {competitor.batches} batches • {competitor.mentions} mentions
                          </div>
                          {competitor.visibilityRate !== undefined && (
                            <div className="mt-1">
                              <span className={`text-mono text-xs ${
                                competitor.visibilityRate >= 50 ? 'text-green-400' : 
                                competitor.visibilityRate >= 25 ? 'text-yellow-400' : 
                                'text-red-400'
                              }`}>
                                Visibility: {competitor.visibilityRate}%
                              </span>
                              <div className={`w-full h-1 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-300'} mt-1`}>
                                <div 
                                  className={`h-full rounded-full transition-all duration-300 ${
                                    competitor.visibilityRate >= 50 ? 'bg-green-400' : 
                                    competitor.visibilityRate >= 25 ? 'bg-yellow-400' : 
                                    'bg-red-400'
                                  }`}
                                  style={{ width: `${Math.min(competitor.visibilityRate, 100)}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right ml-2">
                        <div className="flex flex-col items-end">
                          <span className={`text-mono text-sm font-semibold ${
                            competitor.visibilityRate >= 50 ? 'text-green-400' : 
                            competitor.visibilityRate >= 25 ? 'text-yellow-400' : 
                            'text-red-400'
                          }`}>
                            {competitor.visibilityRate}%
                          </span>
                          <div className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>visibility</div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className={`text-mono text-sm ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>{idx + 1}.</span>
                        <span className={`text-mono font-medium truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>{competitor.name}</span>
                      </div>
                      <div className="text-right">
                        <span className={`text-mono text-sm font-semibold ${
                          competitor.visibilityRate >= 50 ? 'text-green-400' : 
                          competitor.visibilityRate >= 25 ? 'text-yellow-400' : 
                          'text-red-400'
                        }`}>
                          {competitor.visibilityRate}%
                        </span>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className={`glass-dark rounded-lg p-6 text-center ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <svg className="w-12 h-12 mx-auto mb-3 text-purple-400/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <p className="text-mono text-sm mb-2">No tracked competitors yet</p>
              <p className="text-mono text-xs">Tracked competitors will appear here once configured</p>
            </div>
          )}
        </div>

        {/* Sources Analysis */}
        {viewMode === 'detailed' && sourceChartData.length > 0 && (
          <div>
            <h3 className={`text-mono text-md font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
              <svg className="w-4 h-4 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              Source Categories Distribution
            </h3>
            <div className="glass-card rounded-lg p-6">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sourceChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => percent > 5 ? `${name} ${(percent * 100).toFixed(0)}%` : ''}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {sourceChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[entry.name] || COLORS.Unknown} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      contentStyle={{
                        backgroundColor: isDark ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                        border: isDark ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(0, 0, 0, 0.2)',
                        borderRadius: '8px',
                        padding: '12px',
                        boxShadow: isDark ? '0 4px 12px rgba(0, 0, 0, 0.5)' : '0 4px 12px rgba(0, 0, 0, 0.15)',
                      }}
                      labelStyle={{ 
                        color: isDark ? '#ffffff' : '#000000',
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}
                      itemStyle={{
                        color: isDark ? '#ffffff' : '#000000',
                        fontFamily: 'monospace',
                        fontSize: '11px'
                      }}
                      formatter={(value, name, props) => [
                        `${value} citations (${props.payload.domains} domains)`, 
                        props.payload.name
                      ]}
                    />
                    <Legend 
                      verticalAlign="bottom" 
                      height={36}
                      wrapperStyle={{
                        paddingTop: '20px',
                        fontSize: '10px',
                        fontFamily: 'monospace'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Top Sources */}
      {data.topSources.length > 0 && (
        <div className="mt-8">
          <h3 className={`text-mono text-md font-bold ${viewMode === 'simple' ? 'mb-4' : 'mb-6'} flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            <svg className={`w-4 h-4 ${isDark ? 'text-accent-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            Most Referenced Sources
            {viewMode === 'detailed' && (
              <span className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                ({data.metrics.uniqueDomains} unique • {data.metrics.totalSources} total)
              </span>
            )}
          </h3>
          <div className={`grid ${viewMode === 'simple' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'} gap-4`}>
            {data.topSources.slice(0, viewMode === 'simple' ? 5 : 10).map((source, idx) => (
              <div key={source.domain} className="flex items-center justify-between p-3 glass-dark rounded-lg">
                {viewMode === 'detailed' ? (
                  <>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className={`text-mono text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>#{idx + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className={`text-mono font-medium truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>{source.domain}</div>
                        <div className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {source.category} • {source.engines.length} engines • {source.batches} batches
                        </div>
                      </div>
                    </div>
                    <div className="text-right ml-2">
                      <div className={`text-mono text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>{source.count}</div>
                      <div className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>citations</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className={`text-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{idx + 1}.</span>
                      <span className={`text-mono font-medium truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>{source.domain}</span>
                    </div>
                    <span className={`text-mono text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>{source.count}</span>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Best Performing Queries */}
      {data.bestQueries.length > 0 && viewMode === 'detailed' && (
        <div className="mt-8">
          <h3 className={`text-mono text-md font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
            <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            Best Performing Queries (All Time)
          </h3>
          <div className="glass-card rounded-lg p-6">
            <div className="space-y-3">
              {data.bestQueries.map((query, idx) => (
                <div key={idx} className={`flex items-center justify-between py-3 border-b last:border-b-0 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <div className="flex-1 min-w-0">
                    <div className={`text-mono text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>{query.query}</div>
                    <div className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {query.competitors} competitors • 
                      {query.clientMentioned ? ' Client mentioned' : ' Client not mentioned'} • 
                      {query.batchName}
                    </div>
                  </div>
                  <div className="ml-4">
                    <span className={`text-mono text-sm font-bold ${isDark ? 'text-accent-400' : 'text-blue-600'}`}>{query.score}/100</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalDashboard;